import React, { useMemo } from 'react';
import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';

import Helmet from 'react-helmet';

const query = graphql`
  query SEOQuery {
    site: sanitySiteSettings(_id: { regex: "/(drafts.|)siteSettings/" }) {
      title
      description
      keywords
      siteUrl
    }
  }
`;

const SEO = ({ pageTitle, pageDescription, pageKeywords, currentPageURL }) => {
  const data = useStaticQuery(query);

  const { siteUrl, title: siteTitle, description: siteDescription, keywords } = useMemo(
    () => data?.site || {},
    [data],
  );

  const mergedKeywords = useMemo(() => [...(keywords || []), ...(pageKeywords || [])], [
    keywords,
    pageKeywords,
  ]);

  const { origin: defaultOrigin } = useLocation();

  const origin = defaultOrigin || siteUrl;

  const title = useMemo(
    () => (pageTitle && siteTitle !== pageTitle ? `${pageTitle} - ${siteTitle}` : siteTitle),
    [pageTitle, siteTitle],
  );

  const description = useMemo(() => pageDescription || siteDescription, [
    pageDescription,
    siteDescription,
  ]);

  const url = useMemo(() => {
    if (currentPageURL) {
      return `${siteUrl}${currentPageURL}`;
    }

    return siteUrl;
  }, [siteUrl, currentPageURL]);

  const metaTags = useMemo(
    () => [
      {
        name: 'title',
        content: title,
      },
      {
        name: 'description',
        content: description,
      },
      {
        itemprop: 'name',
        content: title,
      },
      {
        itemprop: 'description',
        content: description,
      },
      {
        itemprop: 'image',
        content: `${origin}/og-image.png`,
      },
      {
        name: 'image',
        content: `${origin}/og-image.png`,
      },
      {
        name: 'msapplication-TileColor',
        content: '#3694FF',
      },
      {
        name: 'msapplication-config',
        content: '/browserconig.xml',
      },
      {
        name: 'theme-color',
        content: '#ffffff',
      },
      // Open Graph / Facebook
      {
        property: 'og:url',
        content: url,
      },
      {
        property: 'og:type',
        content: 'website',
      },
      {
        property: 'og:title',
        content: title,
      },
      {
        property: 'og:site_name',
        content: title,
      },
      {
        property: 'og:description',
        content: description,
      },
      {
        property: 'og:image',
        content: `${origin}/og-image.png`,
      },
      {
        property: 'og:image:width',
        content: '1200',
      },
      {
        property: 'og:image:height',
        content: '628',
      },
      // Twitter
      {
        name: 'twitter:card',
        content: 'summary_large_image',
      },
      {
        name: 'twitter:site',
        content: '@fujix_io',
      },
      {
        name: 'twitter:url',
        content: url,
      },
      {
        name: 'twitter:title',
        content: title,
      },
      {
        name: 'twitter:description',
        content: description,
      },
      {
        name: 'twitter:image',
        content: `${origin}/og-image.png`,
      },
    ],
    [title, description, url, origin],
  );

  const meta = useMemo(
    () =>
      metaTags.concat(
        mergedKeywords && mergedKeywords.length > 0
          ? { name: 'keywords', content: mergedKeywords.join(', ') }
          : [],
      ),
    [mergedKeywords, metaTags],
  );

  const titleTemplate = useMemo(
    () => (pageTitle && siteTitle !== pageTitle ? `%s - ${siteTitle}` : '%s'),
    [pageTitle, siteTitle],
  );

  if (!data.site) return null;

  return (
    <Helmet
      htmlAttributes={{ lang: 'en' }}
      title={pageTitle || siteTitle}
      titleTemplate={titleTemplate}
      meta={meta}
    >
      <link rel="apple-touch-icon" sizes="180x180" href="/favicon/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon/favicon-16x16.png" />
      <link rel="manifest" href="/favicon/site.webmanifest" />
      <link rel="mask-icon" href="/favicon/safari-pinned-tab.svg" color="#5bbad5" />
    </Helmet>
  );
};

export default SEO;
